// deployment_class: customer_env_allowed
/**
 * useAuth — public hook surface for the rest of the app.
 *
 * Task 11a (frontend password login, MINIMAL) rewires the credential
 * mutations onto the real Better Auth client (`auth-client.ts`):
 *   - signup  → authClient.signUp.email
 *   - login   → authClient.signIn.email
 *   - logout  → authClient.signOut
 * After a successful mutation the hook calls `refresh()`, which reads the
 * authenticated actor from the V3 `/v3/auth/me` endpoint (the Better Auth
 * httpOnly session cookie is sent automatically with `credentials: 'include'`).
 *
 * SESSION STATE is owned by `/v3/auth/me`, NOT by a client-decoded JWT. The
 * legacy `jwt.ts` decode helper is deleted; Better Auth is the sole
 * authenticator on `main`.
 *
 * SCOPE: password-only baseline. MFA / passkey are Task 11b.
 */
import { useCallback } from 'react';
import { useAuthStore } from './auth-store';
import type { AuthOrgMembership, AuthUser } from './auth-store';
import { authClient, authErrorMessage, serverSignOut } from './auth-client';
import { setAuthErrorHandler } from '../api/api-client-base';

// ─────────────────────────────────────────────────────────────────────────────
// GLOBAL 401 → unauthenticated (S-06 step 2)
// ─────────────────────────────────────────────────────────────────────────────
// `setAuthErrorHandler` shipped exported and documented — "the AuthProvider can
// use this to flip the auth store to 'unauthenticated' on any 401 from any
// endpoint" — and had ZERO callers, so `authErrorHandler` stayed permanently
// null and the guard in `apiFetch` never fired. A session that died mid-work
// therefore surfaced as whatever each individual call site did with a thrown
// AuthError, with no global recovery anywhere.
//
// That is also what made a short idle timeout unshippable: shortening it would
// have stranded users on an unhandled error instead of a re-auth prompt. This
// registration is the precondition for that change, not a cosmetic one.
//
// Registered at MODULE SCOPE, not in a component effect: the handler must be
// live for the very first request the app makes (the bootstrap `/v3/auth/me`),
// which can resolve before any provider effect has run.
//
// Only 401 reaches here. Step-up-required is 403 (`AuthStrengthRequiredError`),
// so an action that needs re-authentication does NOT tear down the session.
setAuthErrorHandler(() => {
  // Idempotent: repeated 401s (a dashboard fires several calls at once) must not
  // thrash the store and re-render the whole tree once per in-flight request.
  if (useAuthStore.getState().status === 'unauthenticated') return;
  useAuthStore.getState().setUnauthenticated();
});

export interface AuthMeResponse {
  user: AuthUser;
  scopes: string[];
  organizations: AuthOrgMembership[];
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface SignupCredentials {
  email: string;
  password: string;
  /** Optional display name; Better Auth `name` is required, defaults to email. */
  name?: string;
}

/**
 * A `/v3/auth/me` 200 is only a real session if the body carries a concrete
 * user identity (id + email) and well-formed scope/org arrays. A malformed or
 * forged 200 (e.g. `{ user: null }`) must NOT be trusted as authenticated —
 * fail CLOSED. (Fort Knox Ring 1 · Wave 0 caught the store being coerced into
 * `authenticated` by a null-user 200; see the malformed-/me song.)
 */
function isValidMe(data: unknown): data is AuthMeResponse {
  if (typeof data !== 'object' || data === null) return false;
  const d = data as Record<string, unknown>;
  const user = d['user'];
  if (typeof user !== 'object' || user === null) return false;
  const u = user as Record<string, unknown>;
  return (
    typeof u['id'] === 'string' &&
    u['id'].length > 0 &&
    typeof u['email'] === 'string' &&
    u['email'].length > 0 &&
    // role is part of the /v3/auth/me contract (Role-View Wave 1b) — a body
    // without it is malformed → fail CLOSED (unauthenticated), same posture as a
    // missing id/email. The backend always resolves a role (the `unassigned`
    // floor at worst), so a real session always carries one.
    typeof u['role'] === 'string' &&
    u['role'].length > 0 &&
    Array.isArray(d['scopes']) &&
    Array.isArray(d['organizations'])
  );
}

async function fetchMe(): Promise<AuthMeResponse | null> {
  const res = await fetch('/v3/auth/me', { credentials: 'include' });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`/v3/auth/me failed: ${res.status}`);
  const data = (await res.json()) as unknown;
  // Fail closed on a malformed/forged 200 body — a session is only real with a
  // concrete user identity.
  if (!isValidMe(data)) return null;
  return data;
}

export interface UseAuthReturn {
  user: AuthUser | null;
  scopes: string[];
  organizations: AuthOrgMembership[];
  activeOrgId: string | null;
  status: 'idle' | 'loading' | 'authenticated' | 'unauthenticated' | 'error';
  error: string | null;
  signup: (credentials: SignupCredentials) => Promise<void>;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  switchOrg: (orgId: string | null) => void;
  refresh: (opts?: { silent?: boolean }) => Promise<void>;
}

export function useAuth(): UseAuthReturn {
  const snap = useAuthStore();

  const refresh = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent ?? false;
    // Background re-validation MUST be silent. Flipping status to 'loading' makes
    // the top-level RequireAuthGate render its "Loading…" placeholder, which
    // unmounts and remounts the ENTIRE app — a visible "refresh" that resets the
    // dashboard to its Home view every interval. Only the initial bootstrap (and
    // explicit login/logout/signup) should show the loading transition.
    if (!silent) useAuthStore.getState().setLoading();
    try {
      const me = await fetchMe();
      if (!me) {
        useAuthStore.getState().setUnauthenticated();
        return;
      }
      const activeOrg = me.organizations[0]?.id ?? null;
      useAuthStore.getState().setAuthenticated({
        user: me.user,
        scopes: me.scopes,
        organizations: me.organizations,
        activeOrgId: activeOrg,
      });
    } catch (err) {
      // A transient network error during a SILENT re-validate must NOT tear down
      // the session (status 'error' → RequireAuthGate bounces an active user to
      // login). Keep the current authenticated state; only surface on explicit refresh.
      if (!silent) useAuthStore.getState().setError(err instanceof Error ? err.message : 'Auth error');
    }
  }, []);

  const signup = useCallback(
    async (credentials: SignupCredentials) => {
      const { error } = await authClient.signUp.email({
        email: credentials.email,
        password: credentials.password,
        name: credentials.name?.trim() || credentials.email,
        // Where Better Auth sends the user AFTER they click the verify link in
        // their email. Relative + same-origin → Better Auth's originCheck on
        // /verify-email accepts it; an absolute foreign URL would be rejected
        // (no open-redirect). Login.tsx reads ?verified=1 to confirm they can
        // sign in now. autoSignInAfterVerification is FALSE server-side, so
        // they land here UNAUTHENTICATED and sign in deliberately.
        callbackURL: '/auth/login?verified=1',
      });
      if (error) {
        throw new Error(authErrorMessage(error, 'Account creation failed.'));
      }
      // With email verification required server-side, signup does NOT create a
      // session. We refresh to pick up a session if auto-sign-in is enabled,
      // otherwise the store stays unauthenticated and the caller surfaces the
      // "verify your email" guidance.
      await refresh();
    },
    [refresh],
  );

  const login = useCallback(
    async (credentials: LoginCredentials) => {
      const { error } = await authClient.signIn.email({
        email: credentials.email,
        password: credentials.password,
      });
      if (error) {
        throw new Error(authErrorMessage(error, 'Sign in failed.'));
      }
      await refresh();
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    // Best-effort server-side revoke via an explicit same-origin credentialed
    // POST (see serverSignOut — robust across multi-domain hosts); clear local
    // state regardless of network outcome.
    try {
      await serverSignOut();
    } catch {
      /* ignore — session may already be invalid; local state cleared below */
    }
    useAuthStore.getState().setUnauthenticated();
  }, []);

  const switchOrg = useCallback((orgId: string | null) => {
    useAuthStore.getState().setActiveOrg(orgId);
  }, []);

  return {
    user: snap.user,
    scopes: snap.scopes,
    organizations: snap.organizations,
    activeOrgId: snap.activeOrgId,
    status: snap.status,
    error: snap.error,
    signup,
    login,
    logout,
    switchOrg,
    refresh,
  };
}
